import { DialogTitle } from "@radix-ui/react-dialog";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "./button";
import { ExternalLink, Links } from "./links";
import { Tooltip } from "./tooltip";

const colors = new Map([
  ["function", "text-amber-200"],
  ["hook", "text-amber-200"],
  ["string", "text-red-400"],
  ["object", "text-sky-300"],
  ["prop", "text-sky-200"],
  ["array", "text-sky-300"],
  ["any", "text-sky-200"],
  ["squarebracket", "text-sky-400"],
  ["bracket", "text-amber-400"],
  ["component", "text-emerald-300"],
  ["jsx", "text-stone-500"],
  ["const", "text-sky-500"],
]);

const badgeContent = new Map([
  [
    "pure",
    <span>
      This function runs during the render phase so it must be pure. See{" "}
      <ExternalLink href="https://beta.reactjs.org/learn/keeping-components-pure">
        Keeping Components Pure
      </ExternalLink>
    </span>,
  ],
  [
    "dependency list",
    <span>
      The list of dependencies must have a constant number of items and be
      written inline like <code>[dep1, dep2, dep3]</code>. React will compare
      each dependency with its previous value using the <code>Object.is</code>{" "}
      comparison algorithm.
    </span>,
  ],
  [
    "ref object",
    <span>
      An object with a single property <code>current</code>, representing the
      current value. This value is intentionally mutable, meaning you can both
      read and write to it.
    </span>,
  ],
  // [
  //   "Context object",
  //   <span>
  //     When React renders a component that subscribes to a Context object, it
  //     will read the current context value from the closest matching Provider
  //     above it in the tree.
  //   </span>,
  // ],
] as const);

function SpecialObject({ properties }) {
  return (
    <div className="mt-4 mb-2 w-3/4 rounded-md bg-stone-800 p-2 text-sm">
      <code>{"{"}</code>
      {/* Or only show description, on click of property name */}
      <table className="ml-5 border-separate border-spacing-1">
        <tbody>
          {properties?.map((d) => (
            <tr key={d.name} className="mb-1">
              <th className="mb-1 rounded bg-stone-600 px-1">
                <code className="font-light">{d.name}</code>
              </th>
              <td>:</td>
              <td className="pl-2">{d.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <code>{"}"}</code>
    </div>
  );
}

function Const() {
  return <span className={colors.get("const")}>const</span>;
}

function Returns({ returns, selected, setSelected }) {
  let returnsEl: React.ReactNode | null = null;

  if (Array.isArray(returns)) {
    returnsEl = (
      <span className="font-mono">
        <Const /> <span className={colors.get("squarebracket")}>[</span>
        {returns.map((d, i, a) => (
          <React.Fragment key={d.name}>
            <Button
              className={twMerge(
                colors.get(d.type),
                "rounded",
                selected?.name === d.name ? "underline underline-offset-8" : ""
              )}
              onPress={() => setSelected(d)}
            >
              {d.name}
            </Button>
            {i !== a.length - 1 && ", "}
          </React.Fragment>
        ))}
        <span className={colors.get("squarebracket")}>]</span>
        {" = "}
      </span>
    );
  } else if (
    ["any", "string", "component", "function", "object"].includes(returns.type)
  ) {
    returnsEl = (
      <span>
        <Const />{" "}
        <Button
          onPress={() => setSelected(returns)}
          className={`${colors.get(returns.type)} rounded  ${
            selected?.name === returns.name && "underline underline-offset-8"
          }`}
        >
          {returns.name}
        </Button>{" "}
        ={" "}
      </span>
    );
  }

  return returnsEl;
}

function Parameters({ parameters, selected, setSelected }) {
  if (Array.isArray(parameters)) {
    return (
      <>
        <OpeningBracket />
        {parameters.map((d, i, a) => (
          <React.Fragment key={d.name}>
            <Button
              className={twMerge(
                colors.get(d.type),
                "rounded",
                selected?.name === d.name ? "underline underline-offset-8" : ""
              )}
              onPress={() => setSelected(d)}
              dangerouslySetStyle={{ opacity: d.optional ? 0.8 : 1 }}
            >
              {d.name}
              {d.optional ? "?" : null}
            </Button>
            {i !== a.length - 1 && ", "}
          </React.Fragment>
        ))}
        <ClosingBracket />
      </>
    );
  } else {
    return (
      <>
        <OpeningBracket />
        {[...parameters.values()].map((d, i, a) => (
          <span key={i}>
            {d.type === "function" ? (
              <Button
                className={twMerge(
                  colors.get(d.type),
                  "rounded",
                  selected?.name === d.name
                    ? "underline underline-offset-8"
                    : ""
                )}
                onPress={() => setSelected(d)}
              >
                <InlineFunc returns={d.return} parameters={d.parameters} />
              </Button>
            ) : (
              <Button
                className={twMerge(
                  colors.get(d.type),
                  "rounded",
                  selected?.name === d.name
                    ? "underline underline-offset-8"
                    : ""
                )}
                onPress={() => setSelected(d)}
              >
                {d.name}
              </Button>
            )}
            {i !== a.length - 1 && (
              <span className="text-stone-300">{" or "} </span>
            )}
          </span>
        ))}
        <ClosingBracket />
      </>
    );
  }
}

function ReturnFunction({ func }) {
  const funcReturn = func.return ? (
    <span className="font-mono">
      <Const />{" "}
      <span className={colors.get(func.return.type)}>{func.return.name}</span>
      {" = "}
    </span>
  ) : null;

  const funcName = <span className={colors.get(func.type)}>{func.name}</span>;

  // parameters
  if (Array.isArray(func.parameters)) {
    return (
      <div className="font-mono">
        {funcReturn}
        {funcName}
        <SubParameters parameters={func.parameters} />
      </div>
    );
  } else {
    console.log('not an array?', func.parameters);
    return (
      <div className="font-mono">
        {funcName}
        <AltParameters altParams={[...func.parameters.values()]} />
      </div>
    );
  }
}

function AltParameters({ altParams }) {
  return (
    <>
      <OpeningBracket />
      {altParams.map((v, i, a) => (
        <span key={i}>
          {v.type === "function" ? (
            <Tooltip
              content={v.description}
              className="underline underline-offset-8"
            >
              <InlineFunc returns={v.return} parameters={v.parameters} />
            </Tooltip>
          ) : (
            <Tooltip
              content={<span>{v.description}</span>}
              className="underline underline-offset-8"
            >
              {v.name}
            </Tooltip>
          )}
          {i !== a.length - 1 && (
            <span className="text-stone-300">{" or "} </span>
          )}
        </span>
      ))}
      <ClosingBracket />
    </>
  );
}

function InlineFunc({ parameters, returns }) {
  return (
    <>
      <OpeningBracket />
      {parameters.map((p, i, a) => (
        <span className={colors.get(p.type)} key={i}>
          {p.name}
          {i !== a.length - 1 && ", "}
        </span>
      ))}
      <ClosingBracket />
      <span>{" => "}</span>
      {returns.name}
    </>
  );
}

const bracketColor = colors.get("bracket");

const OpeningBracket = () => <span className={bracketColor}>(</span>;
const ClosingBracket = () => <span className={bracketColor}>)</span>;

const squareBracketColor = colors.get("squarebracket");

const OpeningCurlyBracket = () => (
  <span className={squareBracketColor}>{"{"}</span>
);
const ClosingCurlyBracket = () => (
  <span className={squareBracketColor}>{"}"}</span>
);

function SubParameters({ parameters }) {
  return (
    <>
      <OpeningBracket />
      {parameters.map((d, i, a) => (
        <React.Fragment key={d.name}>
          <Tooltip
            content={<span>{d.description}</span>}
            className={`underline underline-offset-1 ${colors.get(d.type)}`}
          >
            {d.name}
          </Tooltip>
          {i !== a.length - 1 && ", "}
        </React.Fragment>
      ))}
      <ClosingBracket />
    </>
  );
}

function ComponentDescription({ name, props, selected, setSelected }) {
  const componentColor = colors.get("component");
  const objectColor = colors.get("object");
  const jsxColor = colors.get("jsx");

  const childrenProp = props.find((d) => d.name === "children");

  return (
    <div>
      <span className={jsxColor}>{"<"}</span>
      <span className={componentColor}>{name}</span>
      {props
        .filter((d) => d.name !== "children")
        .map((d) => (
          <span key={d.name}>
            {" "}
            <span className={colors.get("prop")}>{d.name}</span>
            {"="}
            <OpeningCurlyBracket />
            <Button
              className={`${objectColor} ${
                selected?.name == d.name && "underline underline-offset-8"
              } rounded`}
              onPress={() => setSelected(d)}
            >
              {d.value}
            </Button>
            <ClosingCurlyBracket />
          </span>
        ))}
      <span className={jsxColor}>{">"}</span>
      {childrenProp ? (
        <>
          {"{"}
          <Button
            className={`rounded   ${
              selected?.name === "children" && "underline underline-offset-8"
            } ${colors.get("object")}`}
            onPress={() => setSelected(childrenProp)}
          >
            children
          </Button>
          {"}</"}
          <span className={componentColor}>{name}</span>
          {">"}
        </>
      ) : null}
      <span hidden className="border-b-emerald-400" />
    </div>
  );
}

export function Details({ data, name }) {
  const [selected, setSelected] = React.useState(null);

  return (
    <div className="px-6 py-2">
      <div className="mx-auto mb-4 w-fit max-w-[60ch]">{data.description}</div>

      <div className="text-center text-lg">
        {data.type === "component" ? (
          <ComponentDescription
            name={name}
            props={data.parameters}
            selected={selected}
            setSelected={(s) => setSelected({ ...s, part: "Prop" })}
          />
        ) : (
          <code className="tracking-tight">
            {data.return ? (
              <Returns
                selected={selected}
                setSelected={(s) => setSelected({ ...s, part: "Returns" })}
                returns={data.return}
              />
            ) : null}

            <DialogTitle className="inline text-2xl text-amber-200">
              {name}
            </DialogTitle>

            <Parameters
              selected={selected}
              setSelected={(s) =>
                setSelected({
                  ...s,
                  part: `${s.optional ? "Optional " : ""}Parameter`,
                })
              }
              parameters={data.parameters}
            />
          </code>
        )}
      </div>
      <section aria-label="links" className="mt-4">
        <div className="max-h-screen min-h-[288px] overflow-auto border-t border-b border-stone-600/80 py-4">
          {selected ? (
            <div className="">
              <article>
                <div className="flex items-baseline gap-1">
                  <h3>
                    <span className="text-lg">{selected.part}</span>
                    {": "}
                    <code className={colors.get(selected.type)}>
                      {selected.name}
                    </code>{" "}
                  </h3>

                  <code className="block text-xs text-stone-300">
                    {Array.isArray(selected.type)
                      ? selected.type.join(" | ")
                      : selected.type}
                  </code>

                  {/* TODO add context on metatype */}
                  {selected.metatype ? (
                    badgeContent.has(selected.metatype) ? (
                      <Tooltip content={badgeContent.get(selected.metatype)}>
                        <code className="ml-1 rounded-sm bg-stone-600 p-1 text-xs text-stone-200">
                          {selected.metatype}
                        </code>
                      </Tooltip>
                    ) : (
                      <code className="ml-1 rounded-sm bg-stone-600 p-1 text-xs text-stone-200">
                        {selected.metatype}
                      </code>
                    )
                  ) : null}
                </div>

                <p className="my-4 max-w-[60ch]">{selected.description}</p>

                {/* HACK ALERT!! */}
                {selected.type === "function" &&
                  selected.name !== "cachedFn" && (
                    <ReturnFunction func={selected} />
                  )}
              </article>

              {["Context object", "ref object", "element"].includes(
                selected.metatype
              ) &&
                "properties" in selected && (
                  <SpecialObject properties={selected.properties} />
                )}
            </div>
          ) : (
            <span className="italic text-stone-500">Nothing selected… </span>
          )}
        </div>
      </section>
      <Links name={name} docs={data.docs} links={data.recommended} />
    </div>
  );
}
